import React from "react";

/**
 * ErrorBoundary — catches render-time exceptions in child tree so a single
 * broken sub-component doesn't blank the whole page in production.
 *
 * Design goals:
 *  - Always return SOMETHING (fallback UI), never bubble up.
 *  - Expose a reset callback (reload) so the user can retry.
 *  - Log to console in dev, and optionally ping /api/log-client-error in prod
 *    (we stay light: just console, no extra endpoint for now).
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info?.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="mx-auto max-w-xl px-6 py-16 text-center" data-testid="error-boundary">
          <div className="text-6xl mb-4">🙏</div>
          <h1 className="font-display text-2xl mb-3">Une erreur temporaire s'est produite</h1>
          <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
            Notre équipe a été notifiée. Rechargez la page, ou contactez-nous à{" "}
            <a href="mailto:moncommerce68@gmail.com" className="text-primary underline">moncommerce68@gmail.com</a> si le problème persiste.
          </p>
          <button
            onClick={() => { try { window.location.reload(); } catch { /* noop */ } }}
            className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-5 py-2.5 text-sm font-semibold rounded-sm"
            data-testid="error-boundary-reload"
          >
            Recharger la page
          </button>
          {process.env.NODE_ENV !== "production" && this.state.error && (
            <pre className="mt-6 text-xs text-left bg-muted/50 border border-border rounded-sm p-3 overflow-x-auto">
              {String(this.state.error?.stack || this.state.error)}
            </pre>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}
